import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  Search,
  Eye,
  X,
  CreditCard,
  ChevronLeft,
  FileText,
  Calendar as CalendarIcon,
  Clock as TimeIcon,
  User as UserIcon,
  Users as UsersIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Briefcase as BriefcaseIcon,
  Bell,
  Send,
  Plus,
  Trash2,
  CheckSquare,
  Square,
} from "lucide-react";
import { financeAPI, educationAPI } from "../api/axiosClient";
import { SendReminderResponse, AdminReceivable } from "../api/types";
import { toast } from "react-hot-toast";
import { SimpleDropdown } from "../components/ui/simple-dropdown";
import MultiSelectDropdown from "../components/ui/multi-select-dropdown";
import { NumericFormat } from "react-number-format";

// Legacy Receivable interface for detail view
interface Receivable {
  receivableId: string;
  eventId: string;
  studentEventId: string;
  studentId: string;
  studentName: string;
  studentNumber?: string;
  classroomName?: string;
  title: string;
  description: string;
  amount: number;
  remainingAmount: number;
  currency: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
  dueDate?: string;
  period?: string;
  paymentType?: string;
  parents: Array<{
    parentId: string;
    parentName: string;
    email: string;
    phoneNumber: string;
    phoneCode: string;
    occupation: string;
    relationType: string;
  }>;
}

interface ReceivablesViewProps {
  onViewDetail?: (receivableId: string) => void;
  onCreateNew?: () => void;
}

const ReceivablesView: React.FC<ReceivablesViewProps> = ({
  onViewDetail,
  onCreateNew,
}) => {
  const [receivables, setReceivables] = useState<AdminReceivable[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedReceivable, setSelectedReceivable] =
    useState<Receivable | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [showReminderModal, setShowReminderModal] = useState<boolean>(false);
  const [selectedReceivableForReminder, setSelectedReceivableForReminder] =
    useState<Receivable | null>(null);
  const [notificationChannels, setNotificationChannels] = useState({
    sms: false,
    push: false,
    email: false,
  });

  // Create receivable modal state
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [loadingStudents, setLoadingStudents] = useState<boolean>(false);
  const [classrooms, setClassrooms] = useState<any[]>([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState<boolean>(false);
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<string[]>(
    [],
  );
  const [searchTerm2, setSearchTerm2] = useState<string>(""); // Modal içi arama
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedSearchStudents, setSelectedSearchStudents] = useState<
    string[]
  >([]);
  const [assignedStudents, setAssignedStudents] = useState<any[]>([]); // Seçilen öğrenciler ve ücretleri
  const [selectedAssignedStudents, setSelectedAssignedStudents] = useState<
    string[]
  >([]);
  const [newReceivable, setNewReceivable] = useState({
    type: 0,
    paymentType: -1,
    currency: 0,
    period: "",
    name: "",
    description: "",
    lastPaymentDate: "",
  });

  // Currency options (numeric values for API)
  const currencyOptions = [
    { id: "0", value: 0, label: "₺ Türk Lirası (TRY)" },
  ];

  // Period (month) options
  const periodOptions = [
    { id: "2026-01", value: "2026-01", label: "Ocak 2026" },
    { id: "2026-02", value: "2026-02", label: "Şubat 2026" },
    { id: "2026-03", value: "2026-03", label: "Mart 2026" },
    { id: "2026-04", value: "2026-04", label: "Nisan 2026" },
    { id: "2026-05", value: "2026-05", label: "Mayıs 2026" },
    { id: "2026-06", value: "2026-06", label: "Haziran 2026" },
    { id: "2026-07", value: "2026-07", label: "Temmuz 2026" },
    { id: "2026-08", value: "2026-08", label: "Ağustos 2026" },
    { id: "2026-09", value: "2026-09", label: "Eylül 2026" },
    { id: "2026-10", value: "2026-10", label: "Ekim 2026" },
    { id: "2026-11", value: "2026-11", label: "Kasım 2026" },
    { id: "2026-12", value: "2026-12", label: "Aralık 2026" },
  ];

  // Ödeme tipleri (numeric values for API)
  const paymentTypes = [
    { id: "0", value: 0, label: "Kayıt Ücreti", isShow: true },
    { id: "1", value: 1, label: "Servis Ücreti", isShow: true },
    { id: "2", value: 2, label: "Yemek Ücreti", isShow: true },
    { id: "3", value: 3, label: "Etkinlik Ücreti", isShow: false },
    { id: "4", value: 4, label: "Kitap Ücreti", isShow: true },
  ];

  const [receivablesPagination, setReceivablesPagination] = useState({
    pageNumber: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  // Fetch receivables data from admin endpoint
  const fetchReceivables = async (
    page: number = 1,
    searchTerm: string = "",
  ) => {
    try {
      setLoading(true);
      const response = await financeAPI.getReceivablesAdmin({
        Q: searchTerm,
        Page: page,
        Size: 11,
        OrderBy: 0,
      });

      if (response.success && response.data) {
        console.log(response.data.items, "__items");
        setReceivables(response.data.items);
        setReceivablesPagination({
          pageNumber: response.data.pageNumber,
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
          hasPreviousPage: response.data.hasPreviousPage,
          hasNextPage: response.data.hasNextPage,
        });
      }
    } catch (error) {
      console.error("Error fetching receivables:", error);
      toast.error("Ödeme İstekleri yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceivables();
  }, []);

  // Search handler
  const handleSearchSubmit = () => {
    fetchReceivables(1, searchTerm);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedReceivable(null);
  };

  // Handle close reminder modal
  const handleCloseReminderModal = () => {
    setShowReminderModal(false);
    setSelectedReceivableForReminder(null);
    setNotificationChannels({
      sms: false,
      push: false,
      email: false,
    });
  };

  // Handle notification channel change
  const handleChannelChange = (
    channel: "sms" | "push" | "email",
    checked: boolean,
  ) => {
    setNotificationChannels((prev) => ({
      ...prev,
      [channel]: checked,
    }));
  };

  // Handle send reminder
  const handleSendReminder = async () => {
    // Check if at least one channel is selected
    if (
      !notificationChannels.sms &&
      !notificationChannels.push &&
      !notificationChannels.email
    ) {
      toast.error("Lütfen en az bir bildirim kanalı seçin");
      return;
    }

    if (!selectedReceivableForReminder) {
      toast.error("Ödeme isteği bulunamadı");
      return;
    }

    try {
      const response = await financeAPI.sendReminder({
        id: selectedReceivableForReminder.receivableId,
        receivableId: [selectedReceivableForReminder.studentId],
        sms: notificationChannels.sms,
        push: notificationChannels.push,
        email: notificationChannels.email,
        isAllUser: false
      });

      if (response.success) {
        // Response data is directly in the response object (axios returns response.data)
        // API response structure matches SendReminderResponse directly
        const reminderResponse = (response.data ||
          response) as SendReminderResponse;
        const message =
          reminderResponse.message || "Hatırlatma başarıyla gönderildi";
        const smsCount = reminderResponse.smsSentCount || 0;
        const pushCount = reminderResponse.pushSentCount || 0;
        const emailCount = reminderResponse.emailSentCount || 0;
        const totalCount = reminderResponse.totalSentCount || 0;

        let detailMessage = message;
        if (totalCount > 0) {
          const channels = [];
          if (smsCount > 0) channels.push(`${smsCount} SMS`);
          if (pushCount > 0) channels.push(`${pushCount} Push`);
          if (emailCount > 0) channels.push(`${emailCount} E-posta`);
          detailMessage = `${message} (${channels.join(", ")})`;
        }

        toast.success(detailMessage);
        handleCloseReminderModal();
      } else {
        toast.error(
          response.message || "Hatırlatma gönderilirken bir hata oluştu",
        );
      }
    } catch (error: any) {
      // Axios interceptor already shows toast error, so we don't need to show it again
      console.error("Error sending reminder:", error);
    }
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    fetchReceivables(page, searchTerm);
  };

  // Fetch students for search (filtered by classrooms)
  const fetchStudentsForSearch = async () => {
    if (selectedClassroomIds.length === 0) {
      setSearchResults([]);
      return;
    }

    setLoadingStudents(true);
    try {
      // Seçilen tüm sınıflar için GradeSectionFilters oluştur
      const selectedClassroomsList = classrooms.filter((c) =>
        selectedClassroomIds.includes(c.id),
      );

      const params: any = {
        Q: searchTerm2,
        Page: 1,
        Size: 100,
        OrderBy: 0,
        SortBy: "StudentNumber",
      };

      if (selectedClassroomsList.length > 0) {
        params.GradeSectionFilters = selectedClassroomsList.map(
          (classroom) => ({
            grade: classroom.grade,
            section: classroom.section,
          }),
        );
      }

      const response = await educationAPI.getStudents(params);
      if (response.success && response.data?.items) {
        // Zaten atanmış öğrencileri filtrele
        const assignedIds = assignedStudents.map((s) => s.studentId || s.id);
        const filteredStudents = response.data.items.filter(
          (student: any) =>
            !assignedIds.includes(student.studentId || student.id),
        );
        setSearchResults(filteredStudents);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Fetch classrooms
  const fetchClassrooms = async () => {
    setLoadingClassrooms(true);
    try {
      const response = await educationAPI.getClassrooms({
        Q: "",
        Page: 1,
        Size: 100,
        OrderBy: 0,
        SortBy: "Name",
      });
      if (response.success && response.data?.items) {
        setClassrooms(
          response.data.items.map((classroom: any) => ({
            id: classroom.id,
            value: classroom.id,
            label: classroom.name,
            name: classroom.name,
            grade: classroom.grade,
            section: classroom.section,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching classrooms:", error);
    } finally {
      setLoadingClassrooms(false);
    }
  };

  // Handle open create modal
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    setNewReceivable({
      type: 0,
      paymentType: -1,
      currency: 0,
      period: "",
      name: "",
      description: "",
      lastPaymentDate: "",
    });
    setSelectedClassroomIds([]);
    setSearchTerm2("");
    setSearchResults([]);
    setSelectedSearchStudents([]);
    setAssignedStudents([]);
    setSelectedAssignedStudents([]);
    fetchClassrooms();
  };

  // Handle close create modal
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewReceivable({
      type: 0,
      paymentType: -1,
      currency: 0,
      period: "",
      name: "",
      description: "",
      lastPaymentDate: "",
    });
    setSelectedClassroomIds([]);
    setSearchTerm2("");
    setSearchResults([]);
    setSelectedSearchStudents([]);
    setAssignedStudents([]);
    setSelectedAssignedStudents([]);
  };

  // Select/Deselect search student
  const handleSelectSearchStudent = (studentId: string) => {
    setSelectedSearchStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  // Select all search students
  const handleSelectAllSearchStudents = () => {
    if (selectedSearchStudents.length === searchResults.length) {
      setSelectedSearchStudents([]);
    } else {
      setSelectedSearchStudents(searchResults.map((s) => s.studentId || s.id));
    }
  };

  // Add selected students to assigned list
  const handleAddSelectedStudents = () => {
    const studentsToAdd = searchResults
      .filter((s) => selectedSearchStudents.includes(s.studentId || s.id))
      .map((s) => ({
        ...s,
        amount: 0, // Her öğrenci için varsayılan ücret
      }));

    setAssignedStudents((prev) => [...prev, ...studentsToAdd]);
    setSelectedSearchStudents([]);
    // Arama sonuçlarını güncelle
    setSearchResults((prev) =>
      prev.filter((s) => !selectedSearchStudents.includes(s.studentId || s.id)),
    );
  };

  // Select/Deselect assigned student
  const handleSelectAssignedStudent = (studentId: string) => {
    setSelectedAssignedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  // Select all assigned students
  const handleSelectAllAssignedStudents = () => {
    if (selectedAssignedStudents.length === assignedStudents.length) {
      setSelectedAssignedStudents([]);
    } else {
      setSelectedAssignedStudents(
        assignedStudents.map((s) => s.studentId || s.id),
      );
    }
  };

  // Remove selected students from assigned list
  const handleRemoveSelectedStudents = () => {
    setAssignedStudents((prev) =>
      prev.filter(
        (s) => !selectedAssignedStudents.includes(s.studentId || s.id),
      ),
    );
    setSelectedAssignedStudents([]);
  };

  // Update student amount
  const handleUpdateStudentAmount = (studentId: string, amount: number) => {
    setAssignedStudents((prev) =>
      prev.map((s) =>
        (s.studentId || s.id) === studentId ? { ...s, amount } : s,
      ),
    );
  };

  // Handle create receivables (bulk - single API call)
  const handleCreateReceivable = async () => {
    // Validation
    if (newReceivable.paymentType < 0) {
      toast.error("Lütfen bir ödeme tipi seçin");
      return;
    }
    if (!newReceivable.name.trim()) {
      toast.error("Lütfen başlık girin");
      return;
    }
    if (!newReceivable.period) {
      toast.error("Lütfen dönem seçin");
      return;
    }
    if (!newReceivable.lastPaymentDate) {
      toast.error("Lütfen son ödeme tarihi girin");
      return;
    }
    if (assignedStudents.length === 0) {
      toast.error("Lütfen en az bir öğrenci seçin");
      return;
    }

    // Her öğrenci için tutar kontrolü
    const studentsWithoutAmount = assignedStudents.filter(
      (s) => !s.amount || s.amount <= 0,
    );
    if (studentsWithoutAmount.length > 0) {
      toast.error("Lütfen tüm öğrenciler için geçerli bir tutar girin");
      return;
    }

    setCreateLoading(true);
    try {
      // Prepare students array for API
      const studentsPayload = assignedStudents.map((student) => ({
        studentId: student.studentId || student.id,
        price: student.amount,
      }));

      // Convert period to ISO date string (first day of selected month)
      const periodDate = new Date(
        `${newReceivable.period}-01T00:00:00.000Z`,
      ).toISOString();

      // Convert lastPaymentDate to ISO date string
      const lastPaymentDateISO = new Date(
        `${newReceivable.lastPaymentDate}T23:59:59.000Z`,
      ).toISOString();

      const response = await financeAPI.createReceivable({
        name: newReceivable.name,
        period: periodDate,
        type: newReceivable.type,
        paymentType: newReceivable.paymentType,
        currency: newReceivable.currency,
        lastPaymentDate: lastPaymentDateISO,
        description: newReceivable.description,
        students: studentsPayload,
      });

      if (response.success) {
        toast.success(
          `${assignedStudents.length} öğrenci için ödeme talebi başarıyla oluşturuldu`,
        );
        handleCloseCreateModal();
        fetchReceivables(1, searchTerm);
      } else {
        toast.error("Ödeme talebi oluşturulurken hata oluştu");
      }
    } catch (error: any) {
      console.error("Error creating receivable:", error);
      toast.error(
        error?.response?.data?.message ||
          "Ödeme talebi oluşturulurken hata oluştu",
      );
    } finally {
      setCreateLoading(false);
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-800";
      case "PartiallyPaid":
        return "bg-blue-100 text-blue-800";
      case "Cancelled":
        return "bg-red-100 text-red-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case "Paid":
        return "Ödendi";
      case "PartiallyPaid":
        return "Kısmen Ödendi";
      case "Cancelled":
        return "İptal Edildi";
      case "Pending":
        return "Beklemede";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B51E0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Ödeme İsteği ara... (Enter ile ara)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchSubmit();
                }
              }}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Receivables Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardDescription>
              {loading
                ? "Yükleniyor..."
                : `Toplam ${receivablesPagination.totalCount || 0} ödeme isteği bulundu`}
            </CardDescription>
            <Button
              className="bg-[#9B51E0] hover:bg-[#8B41D0]"
              onClick={onCreateNew || handleOpenCreateModal}
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Ödeme Talebi
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {receivables.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Ödeme isteği bulunamadı</p>
              <p className="text-sm mt-2">
                Arama kriterlerinizi değiştirmeyi deneyin
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Başlık
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ödeme Tipi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dönem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Para Birimi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Son Ödeme Tarihi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Oluşturulma
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {receivables.map((receivable) => (
                    <tr key={receivable.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {receivable.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {receivable.description}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {receivable.paymentTypeDescription}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {receivable.period
                            ? new Date(receivable.period).toLocaleDateString(
                                "tr-TR",
                                {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                },
                              )
                            : "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {receivable.currency}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {receivable.lastPaymentDate
                            ? new Date(
                                receivable.lastPaymentDate,
                              ).toLocaleDateString("tr-TR", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(receivable.createdAt).toLocaleDateString(
                            "tr-TR",
                            { day: "numeric", month: "short", year: "numeric" },
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (onViewDetail) {
                                onViewDetail(receivable.id);
                              }
                            }}
                            className="text-[#9B51E0] hover:text-[#8B41D0]"
                            title="Detay"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {receivablesPagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700">
                Sayfa {receivablesPagination.pageNumber} /{" "}
                {receivablesPagination.totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    handlePageChange(receivablesPagination.pageNumber - 1)
                  }
                  disabled={!receivablesPagination.hasPreviousPage}
                  className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() =>
                    handlePageChange(receivablesPagination.pageNumber + 1)
                  }
                  disabled={!receivablesPagination.hasNextPage}
                  className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 rotate-180" />
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedReceivable && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FileText className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Ödeme İsteği Detayları
                    </h3>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleCloseModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="p-6">
              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Student Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <UserIcon className="w-5 h-5 text-gray-600" />
                    <h4 className="text-lg font-semibold text-gray-900">
                      Öğrenci Bilgileri
                    </h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Ad Soyad
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {selectedReceivable.studentName}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Öğrenci ID
                        </p>
                        <p className="text-gray-900 font-mono text-sm">
                          {selectedReceivable.studentId}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Event Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <CalendarIcon className="w-5 h-5 text-gray-600" />
                    <h4 className="text-lg font-semibold text-gray-900">
                      Etkinlik Bilgileri
                    </h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Başlık
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {selectedReceivable.title}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Açıklama
                        </p>
                        <p className="text-gray-900 text-sm">
                          {selectedReceivable.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <CalendarIcon className="w-5 h-5 text-gray-600" />
                    <h4 className="text-lg font-semibold text-gray-900">
                      Ödeme Bilgileri
                    </h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Toplam Tutar
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {selectedReceivable.amount.toLocaleString("tr-TR")}{" "}
                          {selectedReceivable.currency}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Para Birimi
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {selectedReceivable.currency}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Durum
                        </p>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedReceivable.status)}`}
                        >
                          {getStatusText(selectedReceivable.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <TimeIcon className="w-5 h-5 text-gray-600" />
                    <h4 className="text-lg font-semibold text-gray-900">
                      Tarih Bilgileri
                    </h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Oluşturulma Tarihi
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {new Date(
                            selectedReceivable.createdAt,
                          ).toLocaleString("tr-TR")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Vade Tarihi
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {selectedReceivable.dueDate
                            ? new Date(
                                selectedReceivable.dueDate,
                              ).toLocaleString("tr-TR")
                            : "Belirtilmemiş"}
                        </p>
                      </div>
                    </div>
                    {selectedReceivable.updatedAt && (
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Son Güncelleme
                          </p>
                          <p className="text-gray-900 font-semibold">
                            {new Date(
                              selectedReceivable.updatedAt,
                            ).toLocaleString("tr-TR")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Parents Information */}
              {selectedReceivable.parents &&
                selectedReceivable.parents.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <UsersIcon className="w-5 h-5 text-gray-600" />
                      <h4 className="text-lg font-semibold text-gray-900">
                        Veli Bilgileri
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedReceivable.parents.map((parent, index) => (
                        <div
                          key={parent.parentId}
                          className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="p-2 bg-gray-200 rounded-lg">
                              <UserIcon className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {parent.parentName}
                              </p>
                              <p className="text-sm text-gray-600 font-medium">
                                {parent.relationType}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <MailIcon className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600">
                                {parent.email}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <PhoneIcon className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600">
                                +{parent.phoneCode} {parent.phoneNumber}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <BriefcaseIcon className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600">
                                {parent.occupation}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && selectedReceivableForReminder && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Bell className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Hatırlatma Gönderimi
                    </h3>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCloseReminderModal}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-6">
              {/* Info Message */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>{selectedReceivableForReminder.studentName}</strong>{" "}
                  için ödeme isteği hatırlatması gönderilecektir.
                </p>
              </div>

              {/* Notification Channels */}
              <div className="space-y-4">
                <Label className="text-sm font-semibold text-gray-900">
                  Bildirim Kanalları
                </Label>
                <p className="text-xs text-gray-500 mb-3">
                  Hatırlatmanın gönderileceği kanalları seçin
                </p>
                <div className="space-y-3">
                  {/* SMS */}
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <Checkbox
                      id="sms"
                      checked={notificationChannels.sms}
                      onCheckedChange={(checked) =>
                        handleChannelChange("sms", checked as boolean)
                      }
                    />
                    <Label
                      htmlFor="sms"
                      className="flex items-center space-x-2 cursor-pointer flex-1"
                    >
                      <PhoneIcon className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">
                        SMS
                      </span>
                    </Label>
                  </div>

                  {/* Push Notification */}
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <Checkbox
                      id="push"
                      checked={notificationChannels.push}
                      onCheckedChange={(checked) =>
                        handleChannelChange("push", checked as boolean)
                      }
                    />
                    <Label
                      htmlFor="push"
                      className="flex items-center space-x-2 cursor-pointer flex-1"
                    >
                      <Bell className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">
                        Push Notification
                      </span>
                    </Label>
                  </div>

                  {/* Email */}
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <Checkbox
                      id="email"
                      checked={notificationChannels.email}
                      onCheckedChange={(checked) =>
                        handleChannelChange("email", checked as boolean)
                      }
                    />
                    <Label
                      htmlFor="email"
                      className="flex items-center space-x-2 cursor-pointer flex-1"
                    >
                      <MailIcon className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">
                        E-posta
                      </span>
                    </Label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={handleCloseReminderModal}>
                  İptal
                </Button>
                <Button
                  onClick={handleSendReminder}
                  className="bg-[#9B51E0] hover:bg-[#8B41D0] text-white"
                  disabled={
                    !notificationChannels.sms &&
                    !notificationChannels.push &&
                    !notificationChannels.email
                  }
                >
                  <Send className="w-4 h-4 mr-2" />
                  Gönder
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Receivable Modal */}
      {showCreateModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1600px] h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Plus className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Yeni Ödeme Talebi
                    </h3>
                    <p className="text-sm text-gray-500">
                      Öğrenci seçin ve ücret belirleyin
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCloseCreateModal}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-hidden p-6 pb-0">
                <div className="grid grid-cols-3 gap-6 h-full">
                  {/* Sol Panel - Temel Bilgiler */}
                  <div className="flex flex-col h-full space-y-4 overflow-y-auto">
                    <Card className="border-0 shadow-none">
                      <CardContent className="px-0 space-y-3 pb-0">
                        {/* Title */}
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-gray-700">
                            Başlık *
                          </Label>
                          <Input
                            value={newReceivable.name}
                            onChange={(e) =>
                              setNewReceivable((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                            placeholder="Ödeme talebi başlığı"
                            className="h-9"
                          />
                        </div>

                        {/* Period Selection */}
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-gray-700">
                            Dönem *
                          </Label>
                          <SimpleDropdown
                            options={periodOptions}
                            value={
                              periodOptions.find(
                                (p) => p.id === newReceivable.period,
                              ) || null
                            }
                            onChange={(option) => {
                              const period = option?.value
                                ? String(option.value)
                                : "";
                              setNewReceivable((prev) => ({ ...prev, period }));
                            }}
                            placeholder="Dönem seçin..."
                          />
                        </div>

                        {/* Payment Type Selection */}
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-gray-700">
                            Ödeme Tipi *
                          </Label>
                          <SimpleDropdown
                            options={paymentTypes.filter((pt) => pt.isShow)}
                            value={
                              paymentTypes.find(
                                (pt) => pt.value === newReceivable.paymentType,
                              ) || null
                            }
                            onChange={(option) => {
                              const paymentType =
                                option?.value !== undefined
                                  ? Number(option.value)
                                  : -1;
                              setNewReceivable((prev) => ({
                                ...prev,
                                paymentType,
                              }));
                            }}
                            placeholder="Ödeme tipi seçin..."
                          />
                        </div>

                        {/* Currency Selection */}
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-gray-700">
                            Para Birimi *
                          </Label>
                          <SimpleDropdown
                            options={currencyOptions}
                            value={
                              currencyOptions.find(
                                (c) => c.value === newReceivable.currency,
                              ) || null
                            }
                            onChange={(option) => {
                              const currency =
                                option?.value !== undefined
                                  ? Number(option.value)
                                  : 0;
                              setNewReceivable((prev) => ({
                                ...prev,
                                currency,
                              }));
                            }}
                            placeholder="Para birimi seçin..."
                          />
                        </div>

                        {/* Son Ödeme Tarihi */}
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-gray-700">
                            Son Ödeme Tarihi *
                          </Label>
                          <Input
                            type="date"
                            value={newReceivable.lastPaymentDate}
                            onChange={(e) =>
                              setNewReceivable((prev) => ({
                                ...prev,
                                lastPaymentDate: e.target.value,
                              }))
                            }
                            className="h-9"
                          />
                        </div>

                        {/* Description */}
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-gray-700">
                            Açıklama
                          </Label>
                          <textarea
                            value={newReceivable.description}
                            onChange={(e) =>
                              setNewReceivable((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }))
                            }
                            placeholder="Ödeme talebi açıklaması"
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B51E0] focus:border-transparent resize-none"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Orta Panel - Öğrenci Ara ve Filtrele */}
                  <div className="flex flex-col h-full">
                    <Card className="flex-1 flex flex-col border-0 shadow-none">
                      <CardHeader className="pb-2 pt-0 px-0">
                        <CardTitle className="text-base flex items-center">
                          <Search className="w-4 h-4 mr-2 text-blue-600" />
                          Öğrenci Ara ve Filtrele
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col space-y-3 px-0">
                        {/* Search Input */}
                        <div className="flex space-x-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                              placeholder="Ad, soyad veya numara ile ara..."
                              value={searchTerm2}
                              onChange={(e) => setSearchTerm2(e.target.value)}
                              className="pl-10 h-9"
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={fetchStudentsForSearch}
                            disabled={selectedClassroomIds.length === 0}
                            className="bg-[#9B51E0] hover:bg-[#8B41D0] text-white h-9"
                          >
                            Filtrele
                          </Button>
                        </div>

                        {/* Classroom Filter - Multiple Select */}
                        <div className="space-y-1">
                          <MultiSelectDropdown
                            options={classrooms}
                            selectedValues={selectedClassroomIds}
                            onChange={(values: string[]) => {
                              setSelectedClassroomIds(values);
                              setSearchResults([]);
                            }}
                            placeholder={
                              loadingClassrooms
                                ? "Yükleniyor..."
                                : "Sınıf/Şube seçin (birden fazla seçilebilir)"
                            }
                            loading={loadingClassrooms}
                            maxHeight="200px"
                          />
                        </div>

                        {/* Add Selected Button */}
                        <Button
                          onClick={handleAddSelectedStudents}
                          disabled={selectedSearchStudents.length === 0}
                          className="bg-green-600 hover:bg-green-700 text-white h-9"
                          size="sm"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Seçili Kayıtları Ekle ({selectedSearchStudents.length}
                          )
                        </Button>

                        {/* Search Results Table */}
                        {loadingStudents ? (
                          <div className="flex-1 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0]"></div>
                          </div>
                        ) : searchResults.length === 0 ? (
                          <div className="flex-1 flex items-center justify-center text-gray-500">
                            <div className="text-center">
                              <UsersIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                              <p className="text-sm">
                                Sınıf seçin ve filtreleyin
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex-1 overflow-hidden border border-gray-200 rounded-lg">
                            <div className="overflow-y-auto h-full">
                              <table className="w-full">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="w-10 px-2 py-2 text-center">
                                      <button
                                        onClick={handleSelectAllSearchStudents}
                                        className="flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
                                        title="Tümünü Seç"
                                      >
                                        {selectedSearchStudents.length ===
                                        searchResults.length ? (
                                          <CheckSquare className="w-4 h-4" />
                                        ) : (
                                          <Square className="w-4 h-4" />
                                        )}
                                      </button>
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                                      Öğrenci
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                                      Sınıf
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {searchResults.map((student) => (
                                    <tr
                                      key={student.studentId || student.id}
                                      className={`hover:bg-gray-50 cursor-pointer ${selectedSearchStudents.includes(student.studentId || student.id) ? "bg-blue-50" : ""}`}
                                      onClick={() =>
                                        handleSelectSearchStudent(
                                          student.studentId || student.id,
                                        )
                                      }
                                    >
                                      <td className="w-10 px-2 py-2 text-center">
                                        {selectedSearchStudents.includes(
                                          student.studentId || student.id,
                                        ) ? (
                                          <CheckSquare className="w-4 h-4 text-blue-600" />
                                        ) : (
                                          <Square className="w-4 h-4 text-gray-400" />
                                        )}
                                      </td>
                                      <td className="px-3 py-2">
                                        <div>
                                          <span className="text-sm font-medium text-gray-900">
                                            {student.firstName}{" "}
                                            {student.lastName}
                                          </span>
                                          <p className="text-xs text-gray-500">
                                            {student.studentNumber || "-"}
                                          </p>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-600">
                                        {student.classroomName ||
                                          student.className ||
                                          "-"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Sağ Panel - Seçilen Öğrenciler ve Ücretler */}
                  <div className="flex flex-col h-full">
                    <Card className="flex-1 flex flex-col border-0 shadow-none">
                      <CardHeader className="pb-2 pt-0 px-0">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base flex items-center">
                            <UsersIcon className="w-4 h-4 mr-2 text-green-600" />
                            Seçilen Öğrenciler ({assignedStudents.length})
                          </CardTitle>
                          {assignedStudents.length > 0 && (
                            <Button
                              onClick={handleRemoveSelectedStudents}
                              disabled={selectedAssignedStudents.length === 0}
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              size="sm"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Seçili Sil ({selectedAssignedStudents.length})
                            </Button>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col px-0">
                        {assignedStudents.length === 0 ? (
                          <div className="flex-1 flex items-center justify-center text-gray-500 border border-gray-200 rounded-lg">
                            <div className="text-center">
                              <UsersIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                              <p>Henüz öğrenci seçilmedi</p>
                              <p className="text-sm mt-2">
                                Sol panelden öğrenci seçin ve ekleyin
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex-1 overflow-hidden border border-gray-200 rounded-lg">
                            <div className="overflow-y-auto h-full">
                              <table className="w-full">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="w-10 px-2 py-3 text-center">
                                      <button
                                        onClick={
                                          handleSelectAllAssignedStudents
                                        }
                                        className="flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
                                        title="Tümünü Seç"
                                      >
                                        {selectedAssignedStudents.length ===
                                        assignedStudents.length ? (
                                          <CheckSquare className="w-4 h-4" />
                                        ) : (
                                          <Square className="w-4 h-4" />
                                        )}
                                      </button>
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                      Öğrenci
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                      Numara
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                      Sınıf
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                      Tutar (
                                      {newReceivable.currency === 0
                                        ? "₺"
                                        : newReceivable.currency === 1
                                          ? "₺"
                                          : "₺"}
                                      )
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {assignedStudents.map((student) => (
                                    <tr
                                      key={student.studentId || student.id}
                                      className={`hover:bg-gray-50 ${selectedAssignedStudents.includes(student.studentId || student.id) ? "bg-red-50" : ""}`}
                                    >
                                      <td className="w-10 px-2 py-3 text-center">
                                        <button
                                          onClick={() =>
                                            handleSelectAssignedStudent(
                                              student.studentId || student.id,
                                            )
                                          }
                                          className="flex items-center justify-center"
                                        >
                                          {selectedAssignedStudents.includes(
                                            student.studentId || student.id,
                                          ) ? (
                                            <CheckSquare className="w-4 h-4 text-red-600" />
                                          ) : (
                                            <Square className="w-4 h-4 text-gray-400" />
                                          )}
                                        </button>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className="font-medium text-gray-900">
                                          {student.firstName} {student.lastName}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-600">
                                        {student.studentNumber}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-600">
                                        {student.classroomName ||
                                          student.className ||
                                          "-"}
                                      </td>
                                      <td className="px-4 py-3">
                                        <NumericFormat
                                          value={student.amount || ""}
                                          customInput={Input}
                                          thousandSeparator="."
                                          decimalSeparator=","
                                          decimalScale={2}
                                          fixedDecimalScale
                                          allowNegative={false}
                                          onValueChange={(values: any) => {
                                            handleUpdateStudentAmount(
                                              student.studentId || student.id,
                                              values?.floatValue ?? 0,
                                            );
                                          }}
                                          placeholder="0,00"
                                          className="w-28 h-8 text-sm"
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end p-6 border-t flex-shrink-0 space-x-3">
                <Button variant="outline" onClick={handleCloseCreateModal}>
                  İptal
                </Button>
                <Button
                  onClick={handleCreateReceivable}
                  className="bg-[#9B51E0] hover:bg-[#8B41D0] text-white"
                  disabled={createLoading || assignedStudents.length === 0}
                >
                  {createLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Oluşturuluyor...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Oluştur ({assignedStudents.length} Öğrenci)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivablesView;
